// Actie-/kortingsmotor. Pure functies zodat exact dezelfde berekening op de
// client (winkelwagen-weergave) én op de server (checkout, echte afrekening)
// draait. Acties worden beheerd in Firestore (collectie `promotions`) via het
// admin-scherm /admin/acties.

export type PromotionType = "x_plus_y" | "percent" | "amount";
export type PromotionScope = "all" | "brand" | "products" | "category";

export interface Promotion {
  id: string;
  name: string;
  type: PromotionType;
  active: boolean;
  startDate?: string | null; // ISO-datum, optioneel
  endDate?: string | null;
  scope: PromotionScope;
  scopeValues: string[];     // merknamen (brand), product-slugs/ids (products) of categorie-slugs (category)
  // Alleen bij scope "category", en alleen in de uitvoer van de server (lib/promotions-server.ts):
  // de producten die onder de gekozen categorieën en hun subcategorieën vallen, plus de
  // categorienamen voor de uitlegteksten. Wordt niet in Firestore bewaard. Ontbreekt de lijst,
  // dan raakt een categorie-actie niets (liever geen korting dan een verkeerde).
  productSlugs?: string[];
  scopeLabels?: string[];
  buyQty?: number;           // x_plus_y: koop N …
  freeQty?: number;          // … krijg M gratis
  percent?: number;          // percent: % korting op matchende artikelen
  amount?: number;           // amount: vast bedrag korting op matchende artikelen
  label?: string;            // tonen op productpagina/kaart, bijv. "2+1 gratis"
  cartMessage?: string | null; // optionele uitleg in winkelwagen/op productpagina (max 200 tekens); leeg = standaardzin
}

// Minimale artikel-vorm die zowel CartItem als checkout-items delen.
export interface PromoItem {
  brand?: string;
  slug?: string;
  productId?: string;
  price: number;
  quantity: number;
}

const norm = (s: unknown) => String(s ?? "").trim().toLowerCase();

// Winkel + klanten zitten in Nederland; start-/einddatum van een actie zijn
// kalenderdagen in die tijdzone. Vergelijk daarom als NL-kalenderdatum i.p.v.
// als absoluut tijdstip — anders start een actie pas op de ingestelde datum
// zodra het ook middernacht in UTC is (op een productieserver die in UTC
// draait scheelt dat tot 2 uur), terwijl de admin "vandaag" invulde.
const NL_TZ = "Europe/Amsterdam";
const nlDateStr = (d: Date) => new Intl.DateTimeFormat("en-CA", { timeZone: NL_TZ }).format(d); // YYYY-MM-DD

export function isPromotionLive(p: Promotion, now: Date = new Date()): boolean {
  if (!p.active) return false;
  const today = nlDateStr(now);
  if (p.startDate && today < p.startDate) return false;
  if (p.endDate && today > p.endDate) return false; // t/m einddag
  return true;
}

// De lijst van een categorie-actie kan enkele honderden slugs bevatten en wordt per
// artikel bevraagd (winkelraster, winkelwagen): één Set per lijst onthouden.
const slugSets = new WeakMap<string[], Set<string>>();
function productSlugSet(list: string[]): Set<string> {
  let set = slugSets.get(list);
  if (!set) {
    set = new Set(list.map(norm).filter(Boolean));
    slugSets.set(list, set);
  }
  return set;
}

export function itemMatchesPromotion(item: PromoItem, p: Promotion): boolean {
  if (p.scope === "all") return true;
  if (p.scope === "category") {
    if (!Array.isArray(p.productSlugs)) return false;
    const set = productSlugSet(p.productSlugs);
    return set.has(norm(item.slug)) || set.has(norm(item.productId));
  }
  if (p.scope === "brand") return p.scopeValues.map(norm).includes(norm(item.brand));
  if (p.scope === "products") {
    const vals = p.scopeValues.map(norm);
    return vals.includes(norm(item.slug)) || vals.includes(norm(item.productId));
  }
  return false;
}

// Korting van één actie over de matchende artikelen.
function discountForPromotion(items: PromoItem[], p: Promotion): number {
  const matching = items.filter((i) => itemMatchesPromotion(i, p));
  if (matching.length === 0) return 0;

  if (p.type === "x_plus_y") {
    const buy = Math.max(1, Number(p.buyQty) || 0);
    const free = Math.max(1, Number(p.freeQty) || 0);
    const groupSize = buy + free;
    // Alle losse eenheden met hun prijs
    const units: number[] = [];
    matching.forEach((i) => {
      const q = Math.max(0, Math.floor(Number(i.quantity) || 0));
      for (let k = 0; k < q; k++) units.push(Number(i.price) || 0);
    });
    const freeCount = Math.floor(units.length / groupSize) * free;
    if (freeCount <= 0) return 0;
    units.sort((a, b) => a - b); // goedkoopste gratis
    let d = 0;
    for (let k = 0; k < freeCount; k++) d += units[k];
    return d;
  }

  const subtotal = matching.reduce((s, i) => s + (Number(i.price) || 0) * (Math.max(0, Number(i.quantity) || 0)), 0);
  if (p.type === "percent") {
    const pct = Math.min(100, Math.max(0, Number(p.percent) || 0));
    return (subtotal * pct) / 100;
  }
  if (p.type === "amount") {
    return Math.min(subtotal, Math.max(0, Number(p.amount) || 0));
  }
  return 0;
}

export interface PromoResult {
  discount: number;
  applied: { id: string; name: string; label?: string; discount: number }[];
}

// Totale korting over alle actieve acties. Elke actie werkt op zijn eigen
// matchende set; kortingen worden opgeteld.
export function evaluatePromotions(items: PromoItem[], promos: Promotion[], now: Date = new Date()): PromoResult {
  const applied: PromoResult["applied"] = [];
  let discount = 0;
  if (!Array.isArray(promos) || !Array.isArray(items)) return { discount: 0, applied };
  for (const p of promos) {
    if (!isPromotionLive(p, now)) continue;
    const d = Math.round(discountForPromotion(items, p) * 100) / 100;
    if (d > 0) {
      discount += d;
      applied.push({ id: p.id, name: p.name, label: p.label, discount: d });
    }
  }
  return { discount: Math.round(discount * 100) / 100, applied };
}

// Actielabel voor een specifiek product (voor productpagina/kaart). Geeft het
// label van de eerste live actie die op dit product van toepassing is.
export function promoLabelForProduct(
  product: { brand?: string; slug?: string; id?: string },
  promos: Promotion[],
  now: Date = new Date()
): string | null {
  const item: PromoItem = { brand: product.brand, slug: product.slug, productId: product.id, price: 0, quantity: 1 };
  if (!Array.isArray(promos)) return null;
  for (const p of promos) {
    if (!isPromotionLive(p, now)) continue;
    if (itemMatchesPromotion(item, p)) return p.label || p.name;
  }
  return null;
}

// ─── Uitleg van een actie voor de klant (winkelwagen, /winkelmand, productpagina) ───
// Alleen weergave: de rekenlogica hierboven blijft de enige bron voor de korting.
// Alles is afgeleid uit type/buyQty/freeQty/scope/endDate, zodat er nooit een lege
// of onzinnige tekst staat; `cartMessage` overschrijft alleen de "zo werkt het"-zin.

export interface PromoProgress {
  promoId: string;
  groupSize: number;      // buy + free
  matchingUnits: number;  // som quantity van matchende regels
  freeUnits: number;      // gratis stuks nu (zelfde formule als discountForPromotion)
  needMore: number;       // 0 = alle groepen vol; anders stuks erbij voor het eerstvolgende gratis stuk
  state: "progress" | "applied" | "applied_next";
}

export function promotionProgress(items: PromoItem[], promos: Promotion[], now: Date = new Date()): PromoProgress[] {
  const out: PromoProgress[] = [];
  if (!Array.isArray(promos) || !Array.isArray(items)) return out;
  for (const p of promos) {
    if (!isPromotionLive(p, now)) continue; // zelfde controle als evaluatePromotions en promoLabelForProduct
    if (p.type !== "x_plus_y") continue;
    const buy = Math.max(1, Number(p.buyQty) || 0), free = Math.max(1, Number(p.freeQty) || 0), g = buy + free;
    let m = 0;
    for (const i of items) if (itemMatchesPromotion(i, p)) m += Math.max(0, Math.floor(Number(i.quantity) || 0));
    if (m === 0) continue;
    const r = m % g, freeUnits = Math.floor(m / g) * free;
    out.push({ promoId: p.id, groupSize: g, matchingUnits: m, freeUnits, needMore: r === 0 ? 0 : g - r,
      state: freeUnits === 0 ? "progress" : r === 0 ? "applied" : "applied_next" });
  }
  return out;
}

const MAANDEN = ["januari","februari","maart","april","mei","juni","juli","augustus","september","oktober","november","december"];
const kern = (label?: string) => String(label || "").split("(")[0].trim();

/** Kop: "2+1 gratis op Ten Cate · t/m 5 november" (label voor de "(", merk uit scopeValues, einddatum). */
export function promoHeadline(p: Promotion): string {
  const kop = kern(p.label) || (p.type === "x_plus_y" ? `${p.buyQty}+${p.freeQty} gratis` : "Actie");
  let wat = "";
  if (p.scope === "brand" && p.scopeValues.length) wat = p.scopeValues.length <= 2 ? ` op ${p.scopeValues.join(" of ")}` : " op deelnemende merken";
  if (p.scope === "products") wat = " op geselecteerde producten";
  if (p.scope === "category") {
    const namen = p.scopeLabels || [];
    // Staat de categorie al in het label ("10% korting nachtmode"), dan hoeft ze er niet nog eens achter.
    if (namen.length && namen.some((n) => kop.toLowerCase().includes(n.toLowerCase()))) wat = "";
    else wat = namen.length === 0 ? " op geselecteerde categorieën" : namen.length <= 2 ? ` op ${namen.join(" of ")}` : " op deelnemende categorieën";
  }
  let tot = "";
  if (p.endDate && /^\d{4}-\d{2}-\d{2}$/.test(p.endDate)) { const [, m, d] = p.endDate.split("-").map(Number); tot = ` · t/m ${d} ${MAANDEN[m - 1]}`; }
  return `${kop}${wat}${tot}`;
}

export function promoScopeNoun(p: Promotion, n: number): string {
  if (p.scope === "brand" && p.scopeValues.length === 1) return n === 1 ? `${p.scopeValues[0]}-product` : `${p.scopeValues[0]}-producten`;
  if (p.scope === "brand" && p.scopeValues.length <= 3) return `${n === 1 ? "product" : "producten"} van ${p.scopeValues.join(" of ")}`;
  if (p.scope === "brand") return n === 1 ? "product van een deelnemend merk" : "producten van deelnemende merken";
  if (p.scope === "products") return n === 1 ? "deelnemend product" : "deelnemende producten";
  if (p.scope === "category") {
    const namen = p.scopeLabels || [];
    if (namen.length >= 1 && namen.length <= 2) return `${n === 1 ? "product" : "producten"} uit ${namen.join(" of ")}`;
    return n === 1 ? "product uit een deelnemende categorie" : "producten uit deelnemende categorieën";
  }
  return n === 1 ? "artikel" : "artikelen";
}

const goedkoopste = (free: number) => (free === 1 ? "het goedkoopste is gratis" : `de ${free} goedkoopste zijn gratis`);

/** Korte regel voor de popup (past op 1 regel op 360 px: gemeten 268,8 px van 281). */
export function promoPopupLine(p: Promotion, pr: PromoProgress): string {
  const free = Math.max(1, Number(p.freeQty) || 0);
  if (pr.state === "progress") return `Voeg zelf nog ${pr.needMore} toe: ${goedkoopste(free)}`;
  if (pr.state === "applied") return pr.freeUnits === 1 ? "Het goedkoopste artikel is gratis" : `De ${pr.freeUnits} goedkoopste artikelen zijn gratis`;
  const nog = free === 1 ? "nog een gratis artikel" : `nog ${free} gratis artikelen`;
  return `Toegepast. Nog ${pr.needMore} erbij voor ${nog}`;
}

/** Standaarduitleg voor productpagina en /winkelmand als cartMessage leeg is. */
export function promoDefaultMessage(p: Promotion): string | null {
  if (p.type !== "x_plus_y") return null;
  const buy = Math.max(1, Number(p.buyQty) || 0), free = Math.max(1, Number(p.freeQty) || 0), g = buy + free;
  const wat = free === 1 ? "Het goedkoopste artikel is gratis." : `De ${free} goedkoopste artikelen zijn gratis.`;
  return `Voeg zelf ${g} ${promoScopeNoun(p, g)} toe aan je winkelwagen. ${wat} De korting wordt automatisch berekend.`;
}
export const promoMessage = (p: Promotion) => (p.cartMessage && p.cartMessage.trim()) || promoDefaultMessage(p);

/** De eerste live actie die op dit product van toepassing is (voor de uitleg op de productpagina). */
export function promotionForProduct(product: { brand?: string; slug?: string; id?: string }, promos: Promotion[], now: Date = new Date()): Promotion | null {
  const it: PromoItem = { brand: product.brand, slug: product.slug, productId: product.id, price: 0, quantity: 1 };
  if (!Array.isArray(promos)) return null;
  return promos.find((p) => isPromotionLive(p, now) && itemMatchesPromotion(it, p)) ?? null;
}
